import clsx from 'clsx'
import { useCallback } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import type { AccommodationDayView } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import type { AccommodationDayCellSelectHandler } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarLargeDayCell'
import BodyText from 'LuxKit/Typography/BodyText'
import noop from 'lib/function/noop'
import Caption from 'LuxKit/Typography/Caption'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const DayCellContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: ${rem(125)};
`

const Cell = styled(Clickable)`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${rem(4)};
  min-height: ${rem(80)};
  min-width: ${rem(125)};
  width: 100%;
  padding: ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: border-color 0.2s;

  ${mediaHoverable} {
    &:not(.unavailable):hover {
      border: 1px solid ${props => props.theme.palette.neutral.surface.darkForeground};
    }
  }
`

const PriceLine = styled(BodyText)`
  display: flex;
  align-items: baseline;
  gap: ${rem(4)};
  white-space: nowrap;
`

interface Props {
  day: AccommodationDayView<App.CalendarDay>
  onClick?: AccommodationDayCellSelectHandler
}

function FlashHotelSoldOutDatesCarouselDayCell(props: Props) {
  const {
    day,
    onClick = noop,
  } = props
  const intl = useIntl()
  const pricing = day.pricing
  const unavailable = !pricing || day.soldOut

  const handleClick = useCallback(() => {
    onClick(day)
  }, [day, onClick])

  const formattedDate = intl.formatDate(day.date, INTL_DAY_MONTH_NAME_SHORT)
  const formattedWeekday = intl.formatDate(day.date, { weekday: 'short' })
  const unavailableLabel = intl.formatMessage({
    id: 'TXkyIQ7X45UyZ2trgLNQ',
    defaultMessage: 'Unavailable',
    description: 'Unavailable label shown in a sold-out room alternative date carousel day cell',
  })
  const nightsFromLabel = intl.formatMessage(
    {
      id: 'zT_VghfWjhKnT0X9eAK-',
      defaultMessage: '{duration, plural, one {# night from} other {# nights from}}',
      description: 'Stay length prefix shown above the price in an alternative date carousel day cell',
    },
    { duration: day.duration ?? 0 },
  )

  return <DayCellContainer>
    <Caption variant="medium" colour="neutral-two">
      {formattedWeekday}
    </Caption>
    <Cell
      disabled={unavailable}
      onClick={handleClick}
      className={clsx({ unavailable })}
      data-testid={`sold-out-date-carousel-day-${day.date}`}
    >
      <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
        {!unavailable && <Caption variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'ZtrzklY3fDGjGb_R6YbL',
            defaultMessage: 'Check in',
            description: 'Check-in label shown in an available alternative date carousel day cell',
          })}
        </Caption>}
        <Caption variant="medium" colour="neutral-two">
          {formattedDate}
        </Caption>
      </Group>
      <Divider kind="primary" />
      {unavailable && <Caption variant="medium" colour="neutral-three">
        {unavailableLabel}
      </Caption>}
      {!unavailable && pricing && <div>
        <Caption variant="medium" colour="neutral-three">
          {nightsFromLabel}
        </Caption>
        <PriceLine variant="large">
          <FormattedMessage
            id="ocEGzCj-P_EwgD-lj3QT"
            defaultMessage="{price} {totalLabel}"
            description="Total price line shown in an available alternative date carousel day cell"
            values={{
              price: <BodyText key="price" variant="large" weight="bold" colour="highlight-primary-dark" as="span">
                <FormatCurrency value={pricing.price} format="roundedDollar" />
              </BodyText>,
              totalLabel: <Caption key="total" variant="medium" colour="neutral-two" as="span">
                {intl.formatMessage({
                  id: 'vvV35oUprtTHTtf12n3q',
                  defaultMessage: 'Total',
                  description: 'Total price label shown beside the price in an alternative date carousel day cell',
                })}
              </Caption>,
            }}
          />
        </PriceLine>
      </div>}
    </Cell>
  </DayCellContainer>
}

export default React.memo(FlashHotelSoldOutDatesCarouselDayCell)
